import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ImageWithBasePath from '../imageWithBasePath';
import { all_routes } from '../../../feature-module/router/all_routes';
import { useAuth } from '../../../feature-module/auth/AuthProvider';
import axios from 'axios';
import {t} from "../../../lang/index"

// 🔹 Mapeamento dos serviços para as rotas
const SERVICE_ROUTES: Record<string, string> = {
  "Tuk Tuk": all_routes.userTukTukBooking,
  "Car Rental": all_routes.userCarBooking,
  // Outros serviços podem ficar com rota "#"
};

interface CompraUnreadDTO {
  compraId: number;
  naoVisualizadas: number;
  servicoNome: string;
}

const Sidebar = () => {
  const { user, isLoggedIn, setUser, setIsLoggedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [subdrop, setSubdrop] = useState(false);
  const [unreadSupport, setUnreadSupport] = useState(0);
  const [unreadPerServico, setUnreadPerServico] = useState<Record<string, number>>({});
  const [totalUnreadBookings, setTotalUnreadBookings] = useState<number>(0);

  useEffect(() => setSubdrop(location.pathname.includes('customer')), [location.pathname]);

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    localStorage.removeItem('token');
    setUser(null);
    setIsLoggedIn(false);
    navigate('/');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const api = axios.create({
      baseURL: BASE_URL,
      headers: { Authorization: `Bearer ${token}` },
    });

    // 🔹 Mensagens de suporte
    api.get('/support/tickets/unread-count')
      .then(res => {
        setUnreadSupport(res.data.count);
      })
      .catch(err => console.error(err));

    // 🔹 Buscar mensagens não lidas de todas as compras do usuário
    api.get('/usuario/mensagens/nao-visualizadas-por-compra')
      .then(res => {
        const unreadList: CompraUnreadDTO[] = res.data;

        const unreadByServico: Record<string, number> = {};
        unreadList.forEach(u => {
          unreadByServico[u.servicoNome] = (unreadByServico[u.servicoNome] || 0) + u.naoVisualizadas;
        });

        setUnreadPerServico(unreadByServico);

        const total = Object.values(unreadByServico).reduce((sum, val) => sum + val, 0);
        setTotalUnreadBookings(total);
      })
      .catch(err => console.error(err));
  }, [BASE_URL]);

  const menuItems = [
    {
      label: t('sidebar.main'), // traduz Main
      items: [
        { name: t('sidebar.dashboard'), icon: 'isax-grid-55', to: all_routes.userDashboard },
        {
          name: t('sidebar.myBookings'),
          icon: 'isax-calendar-tick5',
          badge: totalUnreadBookings > 0 ? totalUnreadBookings : null,
          badgeColor: 'bg-danger',
          sub: Object.keys(unreadPerServico).map(servico => ({
            name: servico, // se quiser traduzir nomes de serviços também, pode mapear usando t('services.'+servico)
            to: SERVICE_ROUTES[servico] || "#",
            badge: unreadPerServico[servico] ?? 0,
            badgeColor: 'bg-danger',
          })),
        },
        { name: t('sidebar.myReviews'), icon: 'isax-magic-star5', to: all_routes.userReviews },
        { name: t('sidebar.wishlist'), icon: 'isax-heart5', to: all_routes.wishlist },
        {
          name: t('sidebar.support'),
          icon: 'isax-headphone',
          to: all_routes.userSupport,
          badge: unreadSupport || null,
          badgeColor: 'bg-danger'
        },
      ],
    },
    {
      label: t('sidebar.finance'),
      items: [
        { name: t('sidebar.wallet'), icon: 'isax-wallet-add-15', to: all_routes.userWallet },
        { name: t('sidebar.payments'), icon: 'isax-money-recive5', to: all_routes.payment },
      ],
    },
    {
      label: t('sidebar.account'),
      items: [
        { name: t('sidebar.myProfile'), icon: 'isax-profile-tick5', to: all_routes.myProfile },
        { name: t('sidebar.notifications'), icon: 'isax-notification-bing5', to: all_routes.notification, badge: '05', badgeColor: 'bg-purple' },
        { name: t('sidebar.settings'), icon: 'isax-setting-25', to: all_routes.profileSettings },
        { name: t('sidebar.preferences'), icon: 'isax-setting-25', to: all_routes.userPreferences },
        { name: t('sidebar.logout'), icon: 'isax-logout-15', onClick: handleLogout },
      ],
    },
  ];

  const renderItem = (item: any) => {
    const isActive = item.to ? location.pathname.includes(item.to) : false;

    if (item.sub) {
      const subActive = item.sub.some((s: any) => location.pathname.includes(s.to));
      return (
        <li className="submenu" key={item.name}>
          <Link
            to="#"
            onClick={() => setSubdrop(!subdrop)}
            className={`d-block ${subdrop || subActive ? 'subdrop active' : ''}`}
          >
            <i className={`isax ${item.icon}`} /> {item.name}
            {item.badge !== null && (
              <span className={`badge rounded-pill ${item.badgeColor || 'bg-danger'}`} style={{ fontSize: '10px', marginLeft: '5px' }}>
                {item.badge}
              </span>
            )}
            <span className="menu-arrow" />
          </Link>
          <ul className={subdrop || subActive ? 'd-block' : ''}>
            {item.sub.map((subItem: any) => (
              <li key={subItem.name} className="d-flex justify-content-between align-items-center">
                <Link
                  to={subItem.to}
                  className={`fs-14 d-inline-flex align-items-center ${location.pathname.includes(subItem.to) ? 'active' : ''}`}
                >
                  {subItem.name}
                </Link>
                {subItem.badge > 0 && (
                  <span className={`badge rounded-pill ${subItem.badgeColor || 'bg-danger'}`} style={{ fontSize: '10px' }}>
                    {subItem.badge}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </li>
      );
    }

    return (
      <li key={item.name}>
        <Link
          to={item.to || '#'}
          onClick={item.onClick}
          className={`d-flex align-items-center position-relative ${isActive ? 'active' : ''}`}
        >
          <i className={`isax ${item.icon}`} /> {item.name}
          {item.badge !== null && (
            <span className={`badge rounded-pill ${item.badgeColor || 'bg-danger'}`} style={{ fontSize: '10px', marginLeft: '5px' }}>
              {item.badge}
            </span>
          )}
        </Link>
      </li>
    );
  };

  return (
    <div className="card user-sidebar mb-4 mb-lg-0 theiaStickySidebar">
      <div className="card-header user-sidebar-header">
        <div className="profile-content rounded-pill d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            {user?.foto?.url ? (
              <img
                src={`${BASE_URL}/uploads/${user.foto.id}`}
                alt="Foto de perfil"
                className="img-fluid avatar avatar-lg rounded-circle flex-shrink-0 me-1"
                style={{ objectFit: 'cover', width: 60, height: 60 }}
              />
            ) : (
              <ImageWithBasePath
                src="assets/img/users/user-01.jpg"
                alt="image"
                className="img-fluid avatar avatar-lg rounded-circle flex-shrink-0 me-1"
              />
            )}
            <div>
              <h6 className="fs-16 mb-0">{isLoggedIn && user ? user.nome || user.email : 'Guest'}</h6>
            </div>
          </div>
          <Link
            to={all_routes.profileSettings}
            className="p-1 rounded-circle btn btn-light d-flex align-items-center justify-content-center"
          >
            <i className="isax isax-edit-2 fs-14" />
          </Link>
        </div>
      </div>

      <div className="card-body user-sidebar-body">
        {menuItems.map((section) => (
          <ul key={section.label}>
            <li>
              <span className="fs-14 text-gray-3 fw-medium mb-2">{section.label}</span>
            </li>
            {section.items.map(renderItem)}
          </ul>
        ))}
      </div>
    </div>
  );
};

export default Sidebar;